import * as React from 'react';
import styled, { css } from 'styled-components';

import { color } from '@creditornot/cb-ingredients/design-tokens';
import { Empty } from '@creditornot/cb-placeholders';
import { Spinner as SpinnerIcon } from '@creditornot/cb-progress';
import { useInView } from 'common/hooks/useInView';

import { DataTableContext } from './DataTableContext';
import { DataTableHeader } from './DataTableHeader';
import { DataTableRow } from './DataTableRow';
import { useRowDrag } from './hooks/useRowDrag';
import { useRowExpansion } from './hooks/useRowExpansion';
import { useRowSelection } from './hooks/useRowSelection';
import {
  ColumnType,
  DataTableReturnType,
  SelectionState,
  TRowModel,
  TableRowAction,
} from './types';
import { getGridTemplateColumns, getRowIds } from './utils';

export type EmptyMessageProps = {
  description?: string;
  footer?: React.ReactNode;
  icon?: React.ReactNode;
  title?: string;
};

export type FetchOnScroll = {
  onFetchMore: () => void;
  hasNextPage?: boolean;
  isFetching: boolean;
};

type Props<Row extends TRowModel<Row>> = {
  className?: string;
  dataScrollRestorationId?: string;
  dataTestId?: string;
  /**
   * Renders a table with the provided data. Each object in the `dataSource` can
   * also contain an optional "subrows" property which is an array of objects
   * representing the sub-rows.
   *
   * @param {Row[]} dataSource - Data to be displayed in the table as rows.
   * Each object can have a "subrows" property containing an array of objects.
   */
  dataSource: Row[];
  /**
   * Enable showing the table header.
   * This is useful when using this component as a list without headers.
   */
  hideHeader?: boolean;
  emptyMessage?: EmptyMessageProps;
  getRowMenu?: (record: Row) => TableRowAction<Row>[];
  /**
   * Callback function that is called when the row selection changes.
   * This function provides the current state of row selection including details about which rows are selected,
   * which are unselected, and counts of main and sub rows unselected if using a bulk selection approach.
   *
   * @param {boolean} value.bulkSelect - Indicates whether bulk selection is enabled.
   * @param {string[]} value.unselected - Array of row identifiers that are currently unselected.
   * @param {RowSelection[]} value.selected - Array of objects representing the rows that are currently selected.
   * @param {number} [value.unselectedMainRowsCount] - Optional count of main rows that are unselected.
   * @param {number} [value.unselectedSubRowsCount] - Optional count of sub rows that are unselected.
   */
  onRowDrag?: () => void;
  style?: React.CSSProperties;
  columns: ColumnType<Row>[];
  /**
   * Optional callback to apply a `data-test-id` to each rendered row element.
   * Useful for scoping per-row queries in tests.
   */
  getRowTestId?: (record: Row) => string;
  /**
   * Pass values to this prop to externally manage the table's selection state.
   * Accepts an array of string IDs or 'ALL' to indicate all rows are selected.
   */
  expandedRows?: string[];
  isLoading?: boolean;
  getHideSubRows?: (row?: Row) => boolean;
  getRowCanExpand?: (row: Row) => boolean;
  renderSubComponent?: (row: Row) => React.ReactNode;
  fetchOnScroll?: FetchOnScroll;
  isStickyHeader?: boolean;
  stickyHeaderOffset?: number;
  hideScrollbars?: boolean;
  rowSelection?: {
    disableBulkSelect?: boolean;
    selectedRows?: string[] | 'ALL';
    disabledRowMessage?: string;
    onRowSelectChange?: (value: 'ALL' | SelectionState) => void;
    isRowDisabled?: (row: Row) => boolean;
  };
};

export type { Props as DataTableProps };

const Root = styled.div<{
  $hideHeader: boolean;
  $isStickyHeader: boolean;
  $stickyHeaderOffset: number;
  $hideScrollbars: boolean;
}>`
  overflow-x: auto;
  width: 100%;
  position: relative;

  ${({ $hideScrollbars }) =>
    $hideScrollbars &&
    css`
      &::-webkit-scrollbar {
        display: none;
      }
    `};

  ${({ $hideHeader, $isStickyHeader, $stickyHeaderOffset }) =>
    !$hideHeader &&
    $isStickyHeader &&
    css`
      height: calc(100vh - ${$stickyHeaderOffset}px);
    `}
`;

const Table = styled.div<{
  $gridTemplateColumns: string;
}>`
  display: grid;
  grid-template-columns: ${({ $gridTemplateColumns }) => $gridTemplateColumns};
  grid-auto-flow: row;
  scroll-snap-type: x mandatory;
`;

const EmptyContainer = styled.div`
  padding: 48px 8px;
  grid-column: 1 / -1;
`;

const EmptyIconContainer = styled.div`
  width: 64px;
  height: 64px;
  border-radius: 32px;
  background-color: ${color.bgSurface};
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    width: 44%;
    fill: ${color.textSubdued};
  }
`;

const LoadingContainer = styled.div<{
  $isInitialLoading?: boolean;
  $isLoadingMore?: boolean;
}>`
  padding: 20px 8px;
  display: none;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  grid-column: 1 / -1;
  width: 100%;
  width: -webkit-fill-available;
  width: -moz-available;

  ${({ $isLoadingMore }) =>
    $isLoadingMore &&
    css`
      display: flex;
      position: fixed;
      bottom: 0;
      z-index: 3;
    `}

  ${({ $isInitialLoading }) =>
    $isInitialLoading &&
    css`
      display: flex;
      position: relative;
    `}
`;

const BottomRow = styled.div`
  height: 20px;
  grid-column: 1 / -1;
`;

export function DataTable<Row extends TRowModel<Row>>({
  className,
  dataTestId,
  dataScrollRestorationId,
  dataSource: data,
  emptyMessage,
  expandedRows: expandedIds,
  hideHeader = false,
  columns: defaultColumns,
  isLoading,
  renderSubComponent,
  getRowMenu,
  onRowDrag,
  getHideSubRows,
  fetchOnScroll,
  isStickyHeader = false,
  stickyHeaderOffset = 0,
  hideScrollbars = false,
  rowSelection,
  getRowTestId,
}: Props<Row>) {
  const { isFetching, onFetchMore, hasNextPage = false } = fetchOnScroll ?? {};
  const { disableBulkSelect, onRowSelectChange, selectedRows, isRowDisabled, disabledRowMessage } =
    rowSelection ?? {};

  const { ref: loadMoreTargetRef } = useInView({
    onInView: () => {
      if (onFetchMore && hasNextPage) {
        void onFetchMore();
      }
    },
    options: {
      rootMargin: '100px',
    },
  });

  const loadMoreEnabled = isFetching && onFetchMore && hasNextPage && data.length > 0;
  const columns = defaultColumns.filter((column) => !column.hidden);

  const selections = useRowSelection({ data, selectedRows, isRowDisabled, onRowSelectChange });
  const rowExpansion = useRowExpansion(expandedIds);
  const rowDrag = useRowDrag(onRowDrag);

  const tableConfig = { ...selections, ...rowExpansion, ...rowDrag } as DataTableReturnType;

  const {
    handleToggleRow,
    handleToggleAllRows,
    handleExpandedToggle,
    expandedRows,
    selectionState,
  } = tableConfig;

  const hasSomeSubRows = React.useMemo(() => !!data.find((d) => d.subRows), [data]);
  const hasSubComponent = !!renderSubComponent;
  const { allRowIds } = getRowIds(data);

  const hasDragSelectColumn = !!onRowSelectChange || !!onRowDrag;
  const hasMenuExpandColumn = !!getRowMenu || hasSubComponent || hasSomeSubRows;

  return (
    <Root
      $isStickyHeader={isStickyHeader}
      $stickyHeaderOffset={stickyHeaderOffset}
      $hideHeader={hideHeader}
      $hideScrollbars={hideScrollbars}
      data-scroll-restoration-id={dataScrollRestorationId}
    >
      <DataTableContext
        value={{
          selectionState,
          hasDragSelectColumn,
          hasMenuExpandColumn,
          handleToggleRow,
        }}
      >
        <Table
          role="table"
          data-test-id={dataTestId}
          className={className}
          $gridTemplateColumns={getGridTemplateColumns(
            hasDragSelectColumn,
            hasMenuExpandColumn,
            columns,
          )}
        >
          {!hideHeader && !isLoading && (
            <DataTableHeader
              columns={columns}
              isStickyHeader={isStickyHeader}
              hasDragSelectColumn={hasDragSelectColumn}
              hasMenuExpandColumn={hasMenuExpandColumn}
              handleToggleAllRows={handleToggleAllRows}
              isSelectDisabled={allRowIds.size === 0 || !!disableBulkSelect}
            />
          )}
          {!isLoading && (
            <>
              {data.map((record, index) => (
                <DataTableRow
                  rowIndex={index}
                  key={`${record.id}-${index}`}
                  record={record}
                  renderSubComponent={renderSubComponent}
                  columns={columns}
                  getRowMenu={getRowMenu}
                  getHideSubRows={getHideSubRows}
                  handleExpandedToggle={handleExpandedToggle}
                  expandedRows={expandedRows}
                  isRowDisabled={isRowDisabled}
                  disabledRowMessage={disabledRowMessage}
                  dataTestId={getRowTestId?.(record)}
                />
              ))}
            </>
          )}
          {Boolean(data.length) && !!fetchOnScroll && <BottomRow ref={loadMoreTargetRef} />}
          <LoadingContainer $isInitialLoading={isLoading} $isLoadingMore={loadMoreEnabled}>
            <SpinnerIcon size="large" />
          </LoadingContainer>
          {data.length === 0 && emptyMessage && !isLoading && (
            <EmptyContainer>
              <Empty
                description={emptyMessage.description}
                footer={emptyMessage.footer}
                image={
                  emptyMessage.icon && <EmptyIconContainer>{emptyMessage.icon}</EmptyIconContainer>
                }
                title={emptyMessage.title}
              />
            </EmptyContainer>
          )}
        </Table>
      </DataTableContext>
    </Root>
  );
}
